import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiPlay, 
  FiClock, 
  FiStar, 
  FiBookOpen,
  FiSearch
} from 'react-icons/fi';
import axios from 'axios';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const StudentMyCourses = () => {
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all'); // all, in-progress, completed

  const fetchEnrolledCourses = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/courses/enrolled');
      
      // Filter out courses that no longer exist
      const validCourses = response.data.filter(enrollment => enrollment.course && enrollment.course._id);
      setEnrolledCourses(validCourses);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching enrolled courses:', error);
      setEnrolledCourses([]);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEnrolledCourses();
  }, [fetchEnrolledCourses]);

  const filteredCourses = enrolledCourses.filter(enrollment => {
    const course = enrollment.course;
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.subtitle?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filter === 'all' || 
                         (filter === 'in-progress' && enrollment.progress < 100) ||
                         (filter === 'completed' && enrollment.progress === 100);
    
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">강의를 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">내 강의</h1>
              <p className="text-gray-600 mt-1">수강 중인 강의를 관리하고 학습을 계속하세요</p>
            </div>
            <Link
              to="/courses"
              className="bg-primary-800 text-white px-6 py-3 rounded-lg font-medium hover:bg-primary-700 transition-colors duration-200 flex items-center space-x-2"
            >
              <FiBookOpen className="h-5 w-5" />
              <span>새 강의 찾기</span>
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filter */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="강의 제목으로 검색..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            
            {/* Filter */}
            <div className="flex space-x-2">
              {[
                { key: 'all', label: '전체' },
                { key: 'in-progress', label: '진행 중' },
                { key: 'completed', label: '완료' }
              ].map((option) => (
                <button
                  key={option.key}
                  onClick={() => setFilter(option.key)}
                  className={`px-4 py-3 rounded-lg font-medium transition-colors ${
                    filter === option.key
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-300'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Courses List */}
        {filteredCourses.length === 0 ? (
          <div className="text-center py-12">
            <FiBookOpen className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              {searchTerm || filter !== 'all' ? '검색 결과가 없습니다' : '수강 중인 강의가 없습니다'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm || filter !== 'all' 
                ? '다른 검색어나 필터를 시도해보세요.' 
                : '새로운 강의를 찾아 학습을 시작해보세요!'
              }
            </p>
            <Link
              to="/courses"
              className="inline-flex items-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
            >
              <FiBookOpen className="h-5 w-5 mr-2" />
              강의 탐색하기
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filteredCourses.map((enrollment) => {
              const course = enrollment.course;
              const isCompleted = enrollment.progress === 100;
              
              return (
                <div key={enrollment._id} className="bg-white rounded-lg shadow-soft border border-gray-200 overflow-hidden">
                  {/* Course Image */}
                  <div className="relative">
                    <img
                      src={course.thumbnail || '/images/default-course.svg'}
                      alt={course.title}
                      className="w-full h-48 object-cover"
                    />
                    {isCompleted && (
                      <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        완료
                      </div>
                    )}
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                      <div className="text-white">
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span>진행률</span>
                          <span className="font-medium">{enrollment.progress}%</span>
                        </div>
                        <div className="w-full bg-white/20 rounded-full h-2">
                          <div
                            className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${enrollment.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Course Content */}
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 line-clamp-2">
                      {course.title}
                    </h3>
                    
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {course.subtitle}
                    </p>

                    {/* Instructor */}
                    <div className="flex items-center space-x-2 mb-4">
                      <img
                        src={course.createdBy?.avatar || '/images/default-avatar.svg'}
                        alt={course.createdBy?.name || 'Unknown'}
                        className="w-6 h-6 rounded-full"
                      />
                      <span className="text-sm text-gray-600">{course.createdBy?.name || 'Unknown'}</span>
                    </div>

                    {/* Course Stats */}
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-6">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center">
                          <FiStar className="h-4 w-4 mr-1 text-yellow-400" />
                          <span>{course.averageRating || 0}</span>
                        </div>
                        <div className="flex items-center">
                          <FiClock className="h-4 w-4 mr-1" />
                          <span>{course.totalDuration || 0}분</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div className="flex space-x-3">
                      <Link
                        to={`/courses/${course._id}/learn`}
                        className="flex-1 flex items-center justify-center px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                      >
                        <FiPlay className="h-4 w-4 mr-2" />
                        {isCompleted ? '다시 보기' : '계속 학습'}
                      </Link>
                      <Link
                        to={`/courses/${course._id}`}
                        className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                      >
                        상세보기
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Stats Summary */}
        {filteredCourses.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow-soft p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">학습 요약</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-primary-600">{filteredCourses.length}</div>
                <div className="text-sm text-gray-600">총 강의 수</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {filteredCourses.filter(c => c.progress === 100).length}
                </div>
                <div className="text-sm text-gray-600">완료한 강의</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {Math.round(filteredCourses.reduce((sum, c) => sum + c.progress, 0) / filteredCourses.length)}%
                </div>
                <div className="text-sm text-gray-600">평균 진행률</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentMyCourses;
